import { useEffect, useRef } from 'react';
import { useSubscription } from '@/features/billing/contexts/SubscriptionContext';
import { useToast } from '@/shared/hooks/use-toast';

/**
 * Cross-Tab Subscription Sync Hook
 * Detects subscription changes in other tabs and auto-refreshes
 * Uses localStorage events for cross-tab communication
 */
export function useCrossTabSubscriptionSync() {
  const { usage, refreshSubscription } = useSubscription();
  const { toast } = useToast();
  const lastKnownSubscriptionRef = useRef<string | null>(null);

  // Track subscription state changes
  useEffect(() => {
    const currentState = JSON.stringify({
      subscribed: usage.subscribed,
      isTrialActive: usage.isTrialActive,
      subscriptionStatus: usage.subscriptionStatus,
      planType: usage.planType,
    });

    // Initialize on first load
    if (lastKnownSubscriptionRef.current === null) {
      lastKnownSubscriptionRef.current = currentState;
      return;
    }

    // Detect local changes and broadcast to other tabs
    if (currentState !== lastKnownSubscriptionRef.current) {
      console.log('[CrossTabSync] Local subscription changed, broadcasting...');
      try {
        localStorage.setItem('subscription_change_event', JSON.stringify({
          timestamp: Date.now(),
          state: currentState,
        }));
      } catch (error) {
        // Handle quota exceeded error gracefully
        if (error instanceof Error && error.name === 'QuotaExceededError') {
          console.warn('[CrossTabSync] localStorage quota exceeded, falling back to polling');
        }
      }
      lastKnownSubscriptionRef.current = currentState;
    }
  }, [usage.subscribed, usage.isTrialActive, usage.subscriptionStatus, usage.planType]);

  // Listen for changes from other tabs
  useEffect(() => {
    const handleStorageChange = async (event: StorageEvent) => {
      // Only respond to subscription_change_event
      if (event.key !== 'subscription_change_event' || !event.newValue) {
        return;
      }

      try {
        const changeData = JSON.parse(event.newValue);
        const timeSinceChange = Date.now() - changeData.timestamp;

        // Ignore stale events (older than 5 seconds)
        if (timeSinceChange > 5000) {
          return;
        }

        console.log('[CrossTabSync] Detected subscription change in another tab');

        // Show toast notification
        toast({
          title: 'Subscription Updated',
          description: 'Your subscription has been updated. Refreshing...',
          duration: 3000,
        });

        // Wait 500ms for toast to show, then refresh
        setTimeout(async () => {
          await refreshSubscription();
        }, 500);

      } catch (error) {
        console.error('[CrossTabSync] Error handling storage event:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [refreshSubscription, toast]);

  return null;
}
